<app-uni-page [parent]="this" pageTitle="Administration: QBO Account Setup" [bodyTemplate]="bodyTemplate"
  [footerTemplate]="footerTemplate"></app-uni-page>

<ng-template #bodyTemplate>
  <div *ngIf="!!companyInfo" class="h-full w-full">
    <p-splitter class="w-full grow" layout="horizontal" styleClass="h-full" [panelSizes]="[35, 65]">

      <!-- ============================================================
           LEFT PANEL — editors + reference (Company Info, mappings,
           API Token). Scrolls independently of the right panel so the
           top of this side stays in view while the user pages through
           the Posting Map on the right.
           ============================================================ -->
      <ng-template pTemplate>
        <div class="h-full overflow-auto pr-4">

          <h4>Company Information</h4>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div>
              <label class="block text-sm text-gray-500">Company Name</label>
              {{ companyInfo.CompanyName }}
            </div>
            <div>
              <label class="block text-sm text-gray-500">Legal Name</label>
              {{ companyInfo.LegalName }}
            </div>
            <div>
              <label class="block text-sm text-gray-500">Company Address</label>
              {{ companyInfo.CompanyAddr?.Line1 }} {{ companyInfo.CompanyAddr?.Line2 }} {{ companyInfo.CompanyAddr?.City }}
              {{ companyInfo.CompanyAddr?.CountrySubDivisionCode }} {{ companyInfo.CompanyAddr?.PostalCode }}
            </div>
            <div>
              <label class="block text-sm text-gray-500">Email Address</label>
              {{ companyInfo.CompanyEmailAddr?.Address }} {{ companyInfo.CustomerCommunicationEmailAddr?.Address }}
            </div>
          </div>

          <!-- ============================================================
               GENERAL LEDGER POSTING CONFIGURATION
               One section, organized by accounting classification. Each
               row maps a Uniformax 'role' to a real QB account or item,
               with the underlying QB-side mechanism shown as a subtle
               'via QB Account' / 'via QB Item' badge.
               ============================================================ -->
          <h4 class="mt-6">General Ledger Posting Configuration</h4>
          <p class="text-sm qbc-muted">
            Each row below maps a Uniformax posting role to a real QuickBooks GL account. Roles are organized
            by their accounting classification. <span class="italic">Used by:</span> shows the QB transactions
            that reference each row; the Posting Map on the right shows the full debit/credit picture of each.
          </p>

          <div *ngFor="let cat of glCategoriesInOrder" class="mt-5">
            <div class="text-sm font-semibold uppercase tracking-wide pb-1 border-b mb-2">
              {{ glCategoryMeta(cat).displayName }}
            </div>
            <p class="text-xs italic mb-3 qbc-muted">{{ glCategoryMeta(cat).blurb }}</p>

            <ng-container *ngFor="let d of glDescriptorsFor(cat)">

              <!-- QBAccount-backed row -->
              <div *ngIf="d.source === 'QBAccount' && qbAccountFor(d) as acct"
                   class="my-3 p-3 qb-detail-card qb-primary-tint">
                <div uni-flex-row class="items-center gap-2">
                  <div class="w-1/3">
                    <div class="font-semibold">{{ d.displayLabel }}</div>
                    <div class="text-xs qbc-muted">via QB Account</div>
                  </div>
                  <div class="flex-1">
                    <p-dropdown class="w-full" [(ngModel)]="acct.qbAccountId" [options]="getAccountsByType(acct.qbType)"
                      optionValue="Id" optionLabel="Name" (onChange)="onAccountChange($event, acct)">
                      <ng-template let-act pTemplate="selectedItem">
                        <div class="w-full">
                          {{ act?.Name }}
                          <span class="text-color-secondary">&nbsp; ({{ act?.AccountSubType }})</span>
                        </div>
                      </ng-template>
                      <ng-template let-act pTemplate="item">
                        {{ act?.Name }}
                        <span class="text-color-secondary">&nbsp; ({{ act?.AccountSubType }})</span>
                      </ng-template>
                    </p-dropdown>
                  </div>
                </div>
                <div *ngIf="d.sublabel" class="mt-1 text-xs qbc-muted">{{ d.sublabel }}</div>
                <div class="mt-2 text-xs italic qbc-muted">
                  <span class="font-semibold not-italic">Used by:</span> {{ usageSummaryFor(d) }}
                </div>
              </div>

              <!-- QBItem-backed row -->
              <div *ngIf="d.source === 'QBItem' && qbItemFor(d) as item"
                   class="my-3 p-3 qb-detail-card qb-primary-tint">
                <div uni-flex-row class="items-center gap-2">
                  <div class="w-1/3">
                    <div class="font-semibold">{{ d.displayLabel }}</div>
                    <div class="text-xs qbc-muted">via QB Item</div>
                  </div>
                  <div class="flex-1">
                    <p-dropdown class="w-full" [(ngModel)]="item.qbItemId" [options]="getItemsByType(item.qbType)"
                      optionValue="Id" optionLabel="Name" (onChange)="onItemChange($event, item)">
                      <ng-template let-it pTemplate="selectedItem">
                        <div class="w-full">
                          {{ it?.Name }}
                          <span class="text-color-secondary">&nbsp; (Account: {{ it?.IncomeAccountRef?.name }})</span>
                        </div>
                      </ng-template>
                      <ng-template let-it pTemplate="item">
                        {{ it?.Name }}
                        <span class="text-color-secondary">&nbsp; (Account: {{ it?.IncomeAccountRef?.name }})</span>
                      </ng-template>
                    </p-dropdown>
                  </div>
                </div>
                <div *ngIf="d.sublabel" class="mt-1 text-xs qbc-muted">{{ d.sublabel }}</div>
                <div class="mt-2 text-xs italic qbc-muted">
                  <span class="font-semibold not-italic">Used by:</span> {{ usageSummaryFor(d) }}
                </div>
              </div>

            </ng-container>
          </div>

          <div class="mt-4 flex gap-4 flex-wrap">
            <div>
              <p-checkbox [(ngModel)]="showAllAccounts" [binary]="true" inputId="showAllAccounts"></p-checkbox>
              <label for="showAllAccounts" class="ml-2 text-sm">Show all QB accounts in dropdowns</label>
            </div>
            <div>
              <p-checkbox [(ngModel)]="showAllItems" [binary]="true" inputId="showAllItems"></p-checkbox>
              <label for="showAllItems" class="ml-2 text-sm">Show all QB items in dropdowns</label>
            </div>
          </div>

          <!-- ============================================================
               API TOKEN
               ============================================================ -->
          <h4 class="mt-6">API Token</h4>
          <div class="text-center mt-4">
            <button pButton type="button" class="p-button-raised" (click)="getNewQbToken()"> Reset QuickBooks Token </button>
            <p class="my-2 text-sm">Refresh the QuickBooks token in the database. Only do this if you have appropriate
              QuickBooks credentials.</p>
          </div>

        </div>
      </ng-template>

      <!-- ============================================================
           RIGHT PANEL — GL POSTING MAP (Scheme E)
           ============================================================ -->
      <ng-template pTemplate>
        <div class="h-full overflow-auto pl-4">

          <h4>GL Posting Map</h4>
          <p class="text-sm qbc-muted">
            Every QuickBooks document Uniformax creates, with its debit and credit lines side-by-side.
            Items marked <span class="italic">(QB automatic)</span> are inferred by QuickBooks from the document type
            (e.g. Accounts Receivable on an Invoice) and are not set by Uniformax. Hand-curated from
            <code>QuickbooksFactory.cs</code>; the QB account/item each role resolves to is shown live from your
            current settings on the left.
          </p>

          <div *ngFor="let group of postingGroups" class="mt-5">
            <div class="text-base font-semibold uppercase tracking-wide pb-1 border-b mb-3">
              {{ group.displayName }}
            </div>
            <p class="text-xs italic mb-3 qbc-muted">{{ group.blurb }}</p>

            <div *ngFor="let txn of group.transactions" class="mb-4 p-4 qb-detail-card">
              <div class="font-semibold text-sm mb-1">{{ txn.displayName }}</div>
              <div class="text-xs mb-2 qbc-muted">
                {{ txn.purpose }}
              </div>
              <div class="text-xs mb-3 qbc-muted">
                <span class="font-semibold">Triggered by:</span> {{ txn.trigger }}
              </div>

              <table class="qb-info-table w-full mb-2">
                <thead>
                  <tr class="qbc-th-row">
                    <th class="text-left pb-1 qbc-th-side">Side</th>
                    <th class="text-left pb-1 qbc-th-role">Role</th>
                    <th class="text-left pb-1">Resolves to QB</th>
                    <th class="text-left pb-1">Line</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let e of txn.entries">
                    <td class="value">
                      <span [ngClass]="{
                        'qbc-qbauto': e.kind === 'qbAuto',
                        'qbc-dr': e.kind !== 'qbAuto' && e.side === 'DR',
                        'qbc-cr': e.kind !== 'qbAuto' && e.side === 'CR'
                      }">
                        {{ e.side }}
                      </span>
                    </td>
                    <td class="value">
                      <ng-container [ngSwitch]="e.kind">
                        <span *ngSwitchCase="'account'">
                          <span class="font-semibold">Account:</span> {{ getDisplayLabelForRole(e.role, e.kind) }}
                        </span>
                        <span *ngSwitchCase="'item'">
                          <span class="font-semibold">Item:</span> {{ getDisplayLabelForRole(e.role, e.kind) }}
                        </span>
                        <span *ngSwitchCase="'qbAuto'" class="qbc-qbauto">
                          (QB automatic)
                        </span>
                      </ng-container>
                    </td>
                    <td class="value">
                      <ng-container [ngSwitch]="e.kind">
                        <span *ngSwitchCase="'account'">{{ getResolvedAccountName(e.role!) }}</span>
                        <span *ngSwitchCase="'item'">{{ getResolvedItemName(e.role!) }}</span>
                        <span *ngSwitchCase="'qbAuto'" class="qbc-muted">—</span>
                      </ng-container>
                    </td>
                    <td class="value text-sm">
                      {{ e.label }}
                      <span *ngIf="e.condition" class="block text-xs italic qbc-muted">
                        {{ e.condition }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div *ngFor="let note of txn.notes ?? []" class="text-xs italic mt-1 qbc-muted">
                <i class="pi pi-info-circle mr-1"></i>{{ note }}
              </div>
            </div>
          </div>

        </div>
      </ng-template>

    </p-splitter>
  </div>

  <!--
    Error fallback: shown when loadData() fails (e.g. expired QB token blocks
    QueryAccountsAndItems / QueryCompanyInfo so the splitter above never
    renders). Includes the Reset Token button here AS WELL — the button also
    lives inside the splitter for normal use, but if the splitter never
    renders, the user needs a way to recover. Without this, the page is a
    chicken-and-egg trap: 'token dead, can't get to the reset-token button'.
  -->
  <div *ngIf="message" class="text-center mt-8">
    <div class="text-lg mb-4">{{ message }}</div>
    <button pButton type="button" class="p-button-raised" (click)="getNewQbToken()">
      Reset QuickBooks Token
    </button>
    <p class="my-2 text-sm">Refresh the QuickBooks token in the database. Only do this if you have appropriate QuickBooks credentials.</p>
  </div>
</ng-template>

<ng-template #footerTemplate>
  <div class="left-buttons">
    <button pButton type="button" class="p-button-raised" [disabled]="!uow.hasChanges()" (click)="save()"> Save
    </button>
    <button pButton type="button" class="p-button-raised" [disabled]="!uow.hasChanges()" (click)="undo()"> Undo Changes
    </button>
    <button pButton type="button" class="p-button-raised" icon="pi pi-arrow-left" (click)="goBack()"> <span>Go
        Back</span> </button>
  </div>
</ng-template>
